<template>
  <div class="kol-calendar">
    <div class="kol-calendar__toolbar">
      <a
        class="kol-calendar__nav-button"
        :title="$t('keyoflifeViews.previous')"
        @click="move(-1)"
      >
        <i class="iconoir-nav-arrow-left"></i>
      </a>
      <a
        class="kol-calendar__nav-button"
        :title="$t('keyoflifeViews.next')"
        @click="move(1)"
      >
        <i class="iconoir-nav-arrow-right"></i>
      </a>
      <a class="kol-calendar__today-button" @click="goToday()">
        {{ $t('keyoflifeViews.today') }}
      </a>
      <div class="kol-calendar__mode-switch">
        <a
          v-for="m in ['month', 'week', 'day']"
          :key="m"
          class="kol-calendar__mode-button"
          :class="{ 'kol-calendar__mode-button--active': mode === m }"
          @click="setMode(m)"
        >
          {{ $t('keyoflifeViews.mode_' + m) }}
        </a>
      </div>
      <div class="kol-calendar__month-label">{{ periodLabel }}</div>
      <div class="kol-calendar__toolbar-right">
        <span v-if="truncated" class="kol-calendar__notice">
          {{ $t('keyoflifeViews.truncated', { count: rows.length }) }}
        </span>
        <div class="kol-calendar__search">
          <i class="iconoir-search kol-calendar__search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="kol-calendar__search-input"
            :placeholder="$t('keyoflifeViews.searchPlaceholder')"
          />
          <a
            v-if="searchQuery"
            class="kol-calendar__search-clear"
            @click="searchQuery = ''"
          >
            <i class="iconoir-cancel"></i>
          </a>
        </div>
        <Dropdown
          :value="view.date_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseDateField')"
          class="kol-calendar__field-dropdown"
          :disabled="readOnly"
          @input="updateDateField($event)"
        >
          <DropdownItem
            v-for="field in dateFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-calendar"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.color_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseColorField')"
          class="kol-calendar__field-dropdown"
          :disabled="readOnly"
          @input="updateColorField($event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noColor')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-fill-color"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.end_date_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseEndDateField')"
          class="kol-calendar__field-dropdown"
          :disabled="readOnly"
          @input="updateEndDateField($event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noEndDate')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in dateFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-calendar"
          ></DropdownItem>
        </Dropdown>
        <a
          class="kol-calendar__nav-button"
          :title="$t('keyoflifeViews.refresh')"
          @click="fetchRows()"
        >
          <i class="iconoir-refresh-double"></i>
        </a>
      </div>
    </div>

    <div v-if="!dateField" class="kol-calendar__empty">
      <i class="iconoir-calendar kol-calendar__empty-icon"></i>
      <div class="kol-calendar__empty-text">
        {{ $t('keyoflifeViews.noDateFieldTitle') }}
      </div>
      <div class="kol-calendar__empty-subtext">
        {{
          dateFields.length > 0
            ? $t('keyoflifeViews.noDateFieldHelp')
            : $t('keyoflifeViews.noDateFieldsInTable')
        }}
      </div>
    </div>

    <div v-else-if="mode === 'month'" class="kol-calendar__body">
      <div v-if="loading" class="loading-absolute-center"></div>
      <div class="kol-calendar__grid">
        <div class="kol-calendar__head">
          <div class="kol-calendar__week-col">
            {{ $t('keyoflifeViews.weekShort') }}
          </div>
          <div
            v-for="(name, index) in dayNames"
            :key="index"
            class="kol-calendar__day-name"
          >
            {{ name }}
          </div>
        </div>
        <div
          v-for="week in weeks"
          :key="week.key"
          class="kol-calendar__week"
          :style="{ height: 100 / weeks.length + '%' }"
        >
          <div class="kol-calendar__week-col kol-calendar__week-number">
            {{ week.number }}
          </div>
          <div class="kol-calendar__days">
            <!-- Nakladka paskow wielodniowych (gdy ustawione end_date_field). -->
            <div
              v-if="isRange"
              class="kol-calendar__bars"
              :style="{ height: weekBarsHeight(week) + 'px' }"
            >
              <a
                v-for="bar in weekBars(week)"
                :key="bar.row.id"
                class="kol-calendar__bar"
                :class="[
                  {
                    'kol-calendar__bar--clip-start': bar.clipStart,
                    'kol-calendar__bar--clip-end': bar.clipEnd,
                    'kol-calendar__event--colored': bar.color,
                  },
                  bar.color ? 'background-color--' + bar.color : '',
                ]"
                :style="{
                  left: bar.leftPct + '%',
                  width: bar.widthPct + '%',
                  top: bar.lane * 20 + 'px',
                }"
                @click="openRow(bar.row)"
              >
                <span class="kol-calendar__bar-title">{{ bar.title }}</span>
              </a>
            </div>
            <div
              v-for="day in week.days"
              :key="day.key"
              class="kol-calendar__day"
              :class="{
                'kol-calendar__day--other-month': !day.inMonth,
                'kol-calendar__day--weekend': day.isWeekend,
                'kol-calendar__day--expanded': expandedDays[day.key],
                'kol-calendar__day--drop-target': dragOverDayKey === day.key,
              }"
              @dragover="onDayDragOver($event, day)"
              @dragleave="onDayDragLeave(day)"
              @drop="onDayDrop(day)"
            >
              <div class="kol-calendar__day-number">
                <a
                  class="kol-calendar__day-link"
                  :class="{ 'kol-calendar__day-number--today': day.isToday }"
                  :title="$t('keyoflifeViews.openDay')"
                  @click.stop="drillToDay(day.key)"
                >
                  {{ day.number }}
                </a>
                <a
                  v-if="canCreate"
                  class="kol-calendar__add"
                  :title="$t('keyoflifeViews.addEvent')"
                  @click.stop="createEventOnDay(day)"
                >
                  <i class="iconoir-plus"></i>
                </a>
              </div>
              <div
                class="kol-calendar__events"
                :style="{ marginTop: weekBarsHeight(week) + 'px' }"
              >
                <a
                  v-for="event in visibleEvents(day)"
                :key="event.row.id"
                class="kol-calendar__event"
                :class="[
                  {
                    'kol-calendar__event--dragging':
                      draggedRow && draggedRow.id === event.row.id,
                    'kol-calendar__event--colored': event.color,
                  },
                  event.color ? 'background-color--' + event.color : '',
                ]"
                :draggable="canEditDate"
                @click="openRow(event.row)"
                @dragstart="onEventDragStart(event.row)"
                @dragend="onEventDragEnd()"
              >
                <div class="kol-calendar__event-line">
                  <span v-if="event.time" class="kol-calendar__event-time">{{
                    event.time
                  }}</span>
                  <span class="kol-calendar__event-title">{{
                    event.title
                  }}</span>
                </div>
                <div
                  v-for="extra in event.extras"
                  :key="extra.fieldId"
                  class="kol-calendar__event-extra"
                >
                  {{ extra.value }}
                </div>
              </a>
              <a
                v-if="hiddenCount(day) > 0"
                class="kol-calendar__more"
                @click="toggleExpand(day.key)"
              >
                {{ $t('keyoflifeViews.more', { count: hiddenCount(day) }) }}
              </a>
              <a
                v-else-if="expandedDays[day.key]"
                class="kol-calendar__more"
                @click="toggleExpand(day.key)"
              >
                {{ $t('keyoflifeViews.less') }}
              </a>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tryb tydzień / dzień: siatka godzinowa -->
    <div v-else class="kol-calendar__timegrid">
      <div v-if="loading" class="loading-absolute-center"></div>
      <!-- Pasek nagłówków dni + wydarzenia całodniowe/wielodniowe -->
      <div class="kol-calendar__tg-head">
        <div class="kol-calendar__tg-gutter"></div>
        <div
          v-for="d in timeGridDays"
          :key="d.key"
          class="kol-calendar__tg-dayhead"
          :class="{ 'kol-calendar__tg-dayhead--today': d.isToday }"
        >
          <a
            class="kol-calendar__tg-dayhead-link"
            :title="$t('keyoflifeViews.openDay')"
            @click="drillToDay(d.key)"
          >
            <span class="kol-calendar__tg-dow">{{ d.dow }}</span>
            <span class="kol-calendar__tg-dom">{{ d.dom }}</span>
          </a>
          <a
            v-if="canCreate"
            class="kol-calendar__add kol-calendar__add--head"
            :title="$t('keyoflifeViews.addEvent')"
            @click.stop="createEventAtTime(d.key, defaultNewMinutes)"
          >
            <i class="iconoir-plus"></i>
          </a>
        </div>
      </div>
      <div class="kol-calendar__tg-allday">
        <div class="kol-calendar__tg-gutter kol-calendar__tg-allday-label">
          {{ $t('keyoflifeViews.allDay') }}
        </div>
        <div
          v-for="d in timeGridDays"
          :key="d.key"
          class="kol-calendar__tg-allday-cell"
          @dragover="onDayDragOver($event, d)"
          @drop="onDayDrop(d)"
        >
          <a
            v-for="event in allDayByDay[d.key] || []"
            :key="event.row.id"
            class="kol-calendar__event"
            :class="[
              event.color ? 'background-color--' + event.color : '',
              { 'kol-calendar__event--colored': event.color },
            ]"
            @click="openRow(event.row)"
          >
            <span class="kol-calendar__event-title">{{ event.title }}</span>
          </a>
        </div>
      </div>
      <!-- Przewijalna siatka godzin -->
      <div ref="tgScroll" class="kol-calendar__tg-scroll">
        <div class="kol-calendar__tg-grid">
          <div class="kol-calendar__tg-hours">
            <div
              v-for="h in 24"
              :key="h"
              class="kol-calendar__tg-hour"
              :style="{ height: hourHeight + 'px' }"
            >
              <span class="kol-calendar__tg-hour-label">{{
                String(h - 1).padStart(2, '0') + ':00'
              }}</span>
            </div>
          </div>
          <div
            v-for="d in timeGridDays"
            :key="d.key"
            class="kol-calendar__tg-daycol"
            :class="{ 'kol-calendar__tg-daycol--target': dragOverDayKey === d.key }"
            @click="onSlotClick(d, $event)"
            @dragover="onTimedDragOver(d, $event)"
            @dragleave="onDayDragLeave(d)"
            @drop="onTimedDrop(d, $event)"
          >
            <div
              v-for="h in 24"
              :key="h"
              class="kol-calendar__tg-slot"
              :style="{ height: hourHeight + 'px' }"
            ></div>
            <!-- linia "teraz" -->
            <div
              v-if="d.isToday && nowOffsetPx !== null"
              class="kol-calendar__tg-now"
              :style="{ top: nowOffsetPx + 'px' }"
            ></div>
            <!-- wydarzenia z godziną -->
            <a
              v-for="ev in timedByDay[d.key] || []"
              :key="ev.row.id"
              class="kol-calendar__tg-event"
              :class="[
                ev.color ? 'background-color--' + ev.color : '',
                {
                  'kol-calendar__event--colored': ev.color,
                  'kol-calendar__tg-event--dragging':
                    draggedRow && draggedRow.id === ev.row.id,
                },
              ]"
              :style="{
                top: ev.topPx + 'px',
                height: eventHeight(ev) + 'px',
                left: ev.leftPct + '%',
                width: ev.widthPct + '%',
              }"
              :draggable="canEditDate && !resizing"
              :title="$t('keyoflifeViews.openRowHint')"
              @click.stop="onEventClick(ev.row)"
              @dragstart="onTimedDragStart(ev, $event)"
              @dragend="onTimedDragEnd()"
            >
              <span class="kol-calendar__tg-event-time">{{ ev.time }}</span>
              <span class="kol-calendar__tg-event-title">{{ ev.title }}</span>
              <span
                v-if="canResizeTime"
                class="kol-calendar__tg-resize"
                :title="$t('keyoflifeViews.resizeHint')"
                @mousedown.stop.prevent="startResize(ev, d, $event)"
                @click.stop
              ></span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <RowEditModal
      ref="rowEditModal"
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="false"
      :visible-fields="fields"
      :hidden-fields="[]"
      :rows="rows"
      :read-only="!canManageRows"
      :show-hidden-fields="false"
      @update="updateValue"
      @refresh-row="refreshRow"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
    ></RowEditModal>
  </div>
</template>

<script>
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import RowService from '@baserow/modules/database/services/row'
import { notifyIf } from '@baserow/modules/core/utils/error'

import CalendarService from '@keyoflife-views/services/calendar'

const VISIBLE_EVENTS_PER_DAY = 4
const DATE_FIELD_TYPES = ['date', 'created_on', 'last_modified']
// Domyslna godzina dla nowo utworzonego wydarzenia (pole z czasem).
const DEFAULT_NEW_EVENT_HOUR = 12
// Ile dodatkowych pol (poza nazwa) pokazac na kafelku wydarzenia.
const MAX_CARD_EXTRA_FIELDS = 2
// Skok przyciagania (snap) w siatce godzin — minuty (jak Google/Outlook: 15).
const SNAP_MINUTES = 15
// Domyslny czas trwania wydarzenia w siatce, gdy brak pola konca (minuty).
const DEFAULT_EVENT_DURATION = 60
const MINUTES_PER_DAY = 24 * 60

export default {
  name: 'KeyOfLifeCalendarView',
  components: { RowEditModal },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    database: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    storePrefix: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      monthAnchor: null,
      anchorDate: null,
      mode: 'month',
      hourHeight: 44,
      nowTick: Date.now(),
      nowTimer: null,
      rows: [],
      totalCount: 0,
      loading: false,
      expandedDays: {},
      draggedRow: null,
      dragOverDayKey: null,
      searchQuery: '',
      fieldOptions: {},
      // Przeciaganie wydarzenia w siatce godzin: ile minut od gory kafelka
      // zlapano (by drop zachowal punkt chwytu) oraz blokada klik-tworzenia
      // tuz po drag/resize.
      timedGrabMin: 0,
      timedDragDuration: DEFAULT_EVENT_DURATION,
      suppressSlotClick: false,
      // Aktywny resize: { rowId, dayKey, startMin, colEl } + podglad wysokosci.
      resizing: null,
      resizePreview: null,
    }
  },
  computed: {
    dateField() {
      return this.fields.find((field) => field.id === this.view.date_field)
    },
    dateFields() {
      return this.fields.filter(
        (field) =>
          DATE_FIELD_TYPES.includes(field.type) ||
          (field.type === 'formula' && field.formula_type === 'date')
      )
    },
    primaryField() {
      return this.fields.find((field) => field.primary)
    },
    colorField() {
      return this.fields.find((field) => field.id === this.view.color_field)
    },
    endDateField() {
      return this.fields.find((field) => field.id === this.view.end_date_field)
    },
    // Tryb zakresow: ustawione pole konca, rozne od pola startu.
    isRange() {
      return (
        !!this.endDateField &&
        !!this.dateField &&
        this.endDateField.id !== this.dateField.id
      )
    },
    singleSelectFields() {
      return this.fields.filter((field) => field.type === 'single_select')
    },
    // Pole konca uzywane jako "godzina konca" w siatce: musi byc edytowalna
    // data Z CZASEM i rozna od pola startu. Wtedy rysujemy czas trwania i
    // pozwalamy na resize. Bez niego — staly DEFAULT_EVENT_DURATION.
    endTimeField() {
      const f = this.endDateField
      if (!f || f.id === this.view.date_field) {
        return null
      }
      if (f.type !== 'date' || f.date_include_time !== true) {
        return null
      }
      return f
    },
    canResizeTime() {
      return this.canManageRows && !!this.endTimeField && this.fieldHasTime
    },
    // Dodatkowe pola pokazywane na kafelku (poza nazwa). MVP: pierwsze
    // niepuste pola wg kolejnosci, z pominieciem primary/daty/koloru.
    cardExtraFields() {
      const excluded = new Set(
        [
          this.primaryField && this.primaryField.id,
          this.view.date_field,
          this.view.color_field,
        ].filter((id) => id != null)
      )
      return this.fields
        .filter((field) => !excluded.has(field.id))
        .slice(0, MAX_CARD_EXTRA_FIELDS)
    },
    fieldHasTime() {
      if (!this.dateField) {
        return false
      }
      return (
        this.dateField.date_include_time === true ||
        ['created_on', 'last_modified'].includes(this.dateField.type)
      )
    },
    // Czy mozemy zapisywac wiersze (uprawnienia + nie tryb tylko-do-odczytu).
    canManageRows() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    canCreateRows() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    // Drag&drop daty ma sens tylko dla edytowalnego pola typu "date"
    // (created_on/last_modified/formula sa tylko do odczytu).
    canEditDate() {
      return (
        this.canManageRows && !!this.dateField && this.dateField.type === 'date'
      )
    },
    canCreate() {
      return this.canCreateRows && !!this.dateField && this.dateField.type === 'date'
    },
    truncated() {
      return this.totalCount > this.rows.length
    },
    monthLabel() {
      const label = new Intl.DateTimeFormat(this.$i18n.locale, {
        month: 'long',
        year: 'numeric',
      }).format(this.monthAnchor)
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    // Etykieta okresu zależna od trybu.
    periodLabel() {
      if (this.mode === 'month') {
        return this.monthLabel
      }
      if (this.mode === 'day') {
        const label = new Intl.DateTimeFormat(this.$i18n.locale, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(this.anchorDate)
        return label.charAt(0).toUpperCase() + label.slice(1)
      }
      // tydzień: zakres pon–niedz
      const days = this.timeGridDays
      if (days.length === 0) {
        return ''
      }
      const fmt = new Intl.DateTimeFormat(this.$i18n.locale, {
        day: 'numeric',
        month: 'short',
      })
      const first = this.keyToDate(days[0].key)
      const last = this.keyToDate(days[days.length - 1].key)
      const year = last.getFullYear()
      return `${fmt.format(first)} – ${fmt.format(last)} ${year}`
    },
    // Dni widoczne w trybie tydzień (7, pon-niedz) lub dzień (1).
    timeGridDays() {
      const anchor = this.anchorDate || new Date()
      const dowFmt = new Intl.DateTimeFormat(this.$i18n.locale, {
        weekday: 'short',
      })
      const todayKey = this.dayKeyFromDate(new Date())
      const make = (date) => ({
        key: this.dayKeyFromDate(date),
        dow: dowFmt.format(date),
        dom: date.getDate(),
        isToday: this.dayKeyFromDate(date) === todayKey,
      })
      if (this.mode === 'day') {
        return [make(new Date(anchor))]
      }
      // tydzień: cofnij do poniedziałku
      const start = new Date(anchor)
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
      return [...Array(7).keys()].map((i) => {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        return make(d)
      })
    },
    // Wydarzenia z godziną (tylko pole z czasem) per dzień, z układem kolumn.
    timedByDay() {
      const byDay = {}
      if (!this.dateField || !this.fieldHasTime) {
        return byDay
      }
      const valueKey = `field_${this.dateField.id}`
      const visibleKeys = new Set(this.timeGridDays.map((d) => d.key))
      const query = this.searchQuery.trim().toLowerCase()
      for (const row of this.rows) {
        const value = row[valueKey]
        if (!value || typeof value !== 'string') {
          continue
        }
        const date = new Date(value)
        if (isNaN(date.getTime())) {
          continue
        }
        const dayKey = this.dayKeyFromDate(date)
        if (!visibleKeys.has(dayKey)) {
          continue
        }
        const title = this.rowTitle(row)
        const extras = this.rowExtras(row)
        if (query) {
          const hay = [title, ...extras.map((e) => e.value)]
            .join(' ')
            .toLowerCase()
          if (!hay.includes(query)) {
            continue
          }
        }
        const minutes = date.getHours() * 60 + date.getMinutes()
        const pad = (n) => String(n).padStart(2, '0')
        // Koniec: z pola "godzina konca" (jesli ma czas i tego samego dnia),
        // inaczej staly czas trwania. Zawsze co najmniej +SNAP_MINUTES.
        let endMin = Math.min(MINUTES_PER_DAY, minutes + DEFAULT_EVENT_DURATION)
        if (this.endTimeField) {
          const endVal = row[`field_${this.endTimeField.id}`]
          if (typeof endVal === 'string' && endVal) {
            const ed = new Date(endVal)
            if (!isNaN(ed.getTime())) {
              const endDayKey = this.dayKeyFromDate(ed)
              if (endDayKey === dayKey) {
                endMin = Math.max(
                  minutes + SNAP_MINUTES,
                  ed.getHours() * 60 + ed.getMinutes()
                )
              } else if (endDayKey > dayKey) {
                endMin = MINUTES_PER_DAY
              }
            }
          }
        }
        if (!byDay[dayKey]) {
          byDay[dayKey] = []
        }
        byDay[dayKey].push({
          row,
          title,
          color: this.rowColor(row),
          startMin: minutes,
          endMin,
          time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
        })
      }
      // pozycjonowanie + układ kolumn dla nakładających się
      for (const dayKey of Object.keys(byDay)) {
        const list = byDay[dayKey].sort((a, b) => a.startMin - b.startMin)
        this.layoutTimedColumns(list)
        for (const ev of list) {
          ev.topPx = (ev.startMin / 60) * this.hourHeight
          ev.heightPx = Math.max(
            18,
            ((ev.endMin - ev.startMin) / 60) * this.hourHeight - 2
          )
        }
      }
      return byDay
    },
    // Wydarzenia całodniowe / bez czasu (gdy pole bez czasu) per dzień.
    allDayByDay() {
      const byDay = {}
      if (!this.dateField) {
        return byDay
      }
      // jeśli pole ma czas, w pasku all-day nic nie pokazujemy (wszystko w siatce)
      if (this.fieldHasTime) {
        return byDay
      }
      const valueKey = `field_${this.dateField.id}`
      const visibleKeys = new Set(this.timeGridDays.map((d) => d.key))
      const query = this.searchQuery.trim().toLowerCase()
      for (const row of this.rows) {
        const parsed = this.parseDateValue(row[valueKey])
        if (parsed === null || !visibleKeys.has(parsed.dayKey)) {
          continue
        }
        const title = this.rowTitle(row)
        if (query && !title.toLowerCase().includes(query)) {
          continue
        }
        if (!byDay[parsed.dayKey]) {
          byDay[parsed.dayKey] = []
        }
        byDay[parsed.dayKey].push({ row, title, color: this.rowColor(row) })
      }
      return byDay
    },
    nowOffsetPx() {
      // pozycja linii "teraz" w siatce; null gdy poza zakresem
      const now = new Date(this.nowTick)
      const minutes = now.getHours() * 60 + now.getMinutes()
      return (minutes / 60) * this.hourHeight
    },
    // Domyslna godzina (w minutach) dla "+ Dodaj" w naglowku siatki.
    defaultNewMinutes() {
      return DEFAULT_NEW_EVENT_HOUR * 60
    },
    dayNames() {
      // 2024-01-01 to poniedzialek — stala kotwica dla nazw dni pon-niedz.
      const formatter = new Intl.DateTimeFormat(this.$i18n.locale, {
        weekday: 'short',
      })
      return [...Array(7).keys()].map((offset) =>
        formatter.format(new Date(2024, 0, 1 + offset))
      )
    },
    eventsByDay() {
      const byDay = {}
      if (!this.dateField) {
        return byDay
      }
      const valueKey = `field_${this.dateField.id}`
      const query = this.searchQuery.trim().toLowerCase()
      for (const row of this.rows) {
        const parsed = this.parseDateValue(row[valueKey])
        if (parsed === null) {
          continue
        }
        // Wydarzenia wielodniowe renderowane sa jako paski (barsByWeek), wiec
        // pomijamy je tutaj, by nie dublowac w komorkach dnia.
        if (this.isRange) {
          const endKey = this.dayKeyOfField(row, this.endDateField)
          if (endKey && endKey > parsed.dayKey) {
            continue
          }
        }
        const title = this.rowTitle(row)
        const extras = this.rowExtras(row)
        // Szukajka: dopasowanie po nazwie i dodatkowych polach (client-side).
        if (query) {
          const haystack = [title, ...extras.map((e) => e.value)]
            .join(' ')
            .toLowerCase()
          if (!haystack.includes(query)) {
            continue
          }
        }
        if (!byDay[parsed.dayKey]) {
          byDay[parsed.dayKey] = []
        }
        byDay[parsed.dayKey].push({
          row,
          time: parsed.time,
          title,
          extras,
          color: this.rowColor(row),
        })
      }
      for (const dayKey of Object.keys(byDay)) {
        byDay[dayKey].sort(
          (a, b) =>
            (a.time || '').localeCompare(b.time || '') ||
            a.title.localeCompare(b.title)
        )
      }
      return byDay
    },
    // Wydarzenia wielodniowe (start < koniec) — renderowane jako paski.
    rangeEvents() {
      if (!this.isRange) {
        return []
      }
      const query = this.searchQuery.trim().toLowerCase()
      const out = []
      for (const row of this.rows) {
        const startKey = this.dayKeyOfField(row, this.dateField)
        const endKey = this.dayKeyOfField(row, this.endDateField)
        if (!startKey || !endKey || endKey <= startKey) {
          continue
        }
        const title = this.rowTitle(row)
        const extras = this.rowExtras(row)
        if (query) {
          const haystack = [title, ...extras.map((e) => e.value)]
            .join(' ')
            .toLowerCase()
          if (!haystack.includes(query)) {
            continue
          }
        }
        out.push({ row, startKey, endKey, title, color: this.rowColor(row) })
      }
      return out
    },
    // Paski przypisane do tygodni z obliczonymi kolumnami i pasami (lanes).
    barsByWeek() {
      const map = {}
      if (!this.isRange) {
        return map
      }
      for (const week of this.weeks) {
        const wStart = week.days[0].key
        const wEnd = week.days[6].key
        const overlapping = this.rangeEvents
          .filter((e) => e.startKey <= wEnd && e.endKey >= wStart)
          .sort(
            (a, b) =>
              a.startKey.localeCompare(b.startKey) ||
              b.endKey.localeCompare(a.endKey)
          )
        const laneEnds = []
        const bars = []
        for (const e of overlapping) {
          const sCol =
            e.startKey < wStart
              ? 0
              : week.days.findIndex((d) => d.key === e.startKey)
          const eCol =
            e.endKey > wEnd
              ? 6
              : week.days.findIndex((d) => d.key === e.endKey)
          let lane = 0
          while (laneEnds[lane] !== undefined && laneEnds[lane] >= sCol) {
            lane++
          }
          laneEnds[lane] = eCol
          bars.push({
            row: e.row,
            title: e.title,
            color: e.color,
            lane,
            leftPct: (sCol / 7) * 100,
            widthPct: ((eCol - sCol + 1) / 7) * 100,
            clipStart: e.startKey < wStart,
            clipEnd: e.endKey > wEnd,
          })
        }
        map[week.key] = { bars, lanes: laneEnds.length }
      }
      return map
    },
    weeks() {
      const firstOfMonth = new Date(
        this.monthAnchor.getFullYear(),
        this.monthAnchor.getMonth(),
        1
      )
      const lastOfMonth = new Date(
        this.monthAnchor.getFullYear(),
        this.monthAnchor.getMonth() + 1,
        0
      )
      // Cofnij do poniedzialku (getDay: niedziela=0).
      const start = new Date(firstOfMonth)
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7))

      const today = this.dayKeyFromDate(new Date())
      const weeks = []
      const cursor = new Date(start)
      while (cursor <= lastOfMonth || weeks.length < 4) {
        const days = []
        for (let i = 0; i < 7; i++) {
          const dayKey = this.dayKeyFromDate(cursor)
          days.push({
            key: dayKey,
            number: cursor.getDate(),
            inMonth: cursor.getMonth() === this.monthAnchor.getMonth(),
            isWeekend: cursor.getDay() === 0 || cursor.getDay() === 6,
            isToday: dayKey === today,
          })
          cursor.setDate(cursor.getDate() + 1)
        }
        weeks.push({
          key: days[0].key,
          number: this.isoWeekNumber(new Date(cursor.getTime() - 86400000)),
          days,
        })
      }
      return weeks
    },
  },
  watch: {
    'view.id'() {
      this.fetchRows()
    },
  },
  created() {
    const now = new Date()
    this.anchorDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    this.monthAnchor = new Date(now.getFullYear(), now.getMonth(), 1)
  },
  mounted() {
    this.fetchRows()
    // odświeżanie linii "teraz" co minutę
    this.nowTimer = setInterval(() => {
      this.nowTick = Date.now()
    }, 60000)
  },
  beforeUnmount() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
    }
    window.removeEventListener('mousemove', this.onResizeMove)
    window.removeEventListener('mouseup', this.onResizeEnd)
  },
  methods: {
    async fetchRows() {
      this.loading = true
      try {
        const { data } = await CalendarService(this.$client).fetchRows({
          viewId: this.view.id,
        })
        this.rows = data.results
        this.totalCount = data.count
        this.fieldOptions = data.field_options || {}
      } catch (error) {
        notifyIf(error, 'rows')
      }
      this.loading = false
    },
    async updateDateField(fieldId) {
      if (fieldId === this.view.date_field) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { date_field: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateColorField(fieldId) {
      if (fieldId === this.view.color_field) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { color_field: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateEndDateField(fieldId) {
      if (fieldId === this.view.end_date_field) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { end_date_field: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    // Klucz dnia (YYYY-MM-DD) dla dowolnego pola daty (z czasem lub bez).
    dayKeyOfField(row, field) {
      const value = row[`field_${field.id}`]
      if (!value || typeof value !== 'string') {
        return null
      }
      const hasTime =
        field.date_include_time === true ||
        ['created_on', 'last_modified'].includes(field.type)
      if (!hasTime) {
        return value.slice(0, 10)
      }
      const date = new Date(value)
      return isNaN(date.getTime()) ? null : this.dayKeyFromDate(date)
    },
    weekBars(week) {
      const entry = this.barsByWeek[week.key]
      return entry ? entry.bars : []
    },
    weekBarsHeight(week) {
      const entry = this.barsByWeek[week.key]
      return entry ? entry.lanes * 20 : 0
    },
    // Kolor wydarzenia = kolor opcji single-select wskazanej w color_field.
    rowColor(row) {
      if (!this.colorField) {
        return null
      }
      const value = row[`field_${this.colorField.id}`]
      return value && value.color ? value.color : null
    },
    // Wartosci dodatkowych pol na kafelku (tekstowa reprezentacja, niepuste).
    rowExtras(row) {
      const extras = []
      for (const field of this.cardExtraFields) {
        const fieldType = this.$registry.get('field', field.type)
        const text = fieldType.toHumanReadableString(
          field,
          row[`field_${field.id}`]
        )
        if (text !== null && text !== undefined && String(text).trim() !== '') {
          extras.push({ fieldId: field.id, value: String(text) })
        }
      }
      return extras
    },
    // Nawigacja zależna od trybu: ±1 miesiąc / tydzień / dzień.
    move(direction) {
      const d = new Date(this.anchorDate)
      if (this.mode === 'month') {
        d.setMonth(d.getMonth() + direction)
      } else if (this.mode === 'week') {
        d.setDate(d.getDate() + 7 * direction)
      } else {
        d.setDate(d.getDate() + direction)
      }
      this.anchorDate = d
      this.monthAnchor = new Date(d.getFullYear(), d.getMonth(), 1)
      this.expandedDays = {}
    },
    setMode(mode) {
      this.mode = mode
      if (mode !== 'month') {
        this.$nextTick(() => this.scrollTimeGridToMorning())
      }
    },
    scrollTimeGridToMorning() {
      const el = this.$refs.tgScroll
      if (el) {
        el.scrollTop = 7 * this.hourHeight
      }
    },
    goToday() {
      const now = new Date()
      this.anchorDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      this.monthAnchor = new Date(now.getFullYear(), now.getMonth(), 1)
      this.expandedDays = {}
      if (this.mode !== 'month') {
        this.$nextTick(() => this.scrollTimeGridToMorning())
      }
    },
    // Wejscie w widok dnia dla wskazanej daty (klik numeru dnia / naglowka).
    drillToDay(dayKey) {
      const date = this.keyToDate(dayKey)
      this.anchorDate = date
      this.monthAnchor = new Date(date.getFullYear(), date.getMonth(), 1)
      this.mode = 'day'
      this.expandedDays = {}
      this.$nextTick(() => this.scrollTimeGridToMorning())
    },
    // --- Siatka godzin: geometria, tworzenie, przeciaganie, resize ----------
    // Minuty (po snapie) z pozycji kursora wzgledem kolumny dnia.
    minutesFromPointer(colEl, clientY) {
      if (!colEl) {
        return this.defaultNewMinutes
      }
      const rect = colEl.getBoundingClientRect()
      let min = ((clientY - rect.top) / this.hourHeight) * 60
      min = Math.max(0, Math.min(MINUTES_PER_DAY - SNAP_MINUTES, min))
      return Math.round(min / SNAP_MINUTES) * SNAP_MINUTES
    },
    eventHeight(ev) {
      if (this.resizePreview && this.resizePreview.rowId === ev.row.id) {
        return Math.max(
          18,
          ((this.resizePreview.endMin - ev.startMin) / 60) * this.hourHeight - 2
        )
      }
      return ev.heightPx
    },
    // Klik w pusty obszar kolumny dnia → nowy wpis o tej godzinie.
    onSlotClick(d, domEvent) {
      if (this.suppressSlotClick) {
        return
      }
      if (!this.canCreate) {
        return
      }
      const minutes = this.fieldHasTime
        ? this.minutesFromPointer(domEvent.currentTarget, domEvent.clientY)
        : this.defaultNewMinutes
      this.createEventAtTime(d.key, minutes)
    },
    // Klik w wydarzenie → edycja (z blokada klik-po-przeciaganiu).
    onEventClick(row) {
      if (this.suppressSlotClick) {
        return
      }
      this.openRow(row)
    },
    composeDateTimeValue(dayKey, minutes) {
      const [year, month, dayNum] = dayKey.split('-').map(Number)
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return new Date(year, month - 1, dayNum, h, m, 0).toISOString()
    },
    async createEventAtTime(dayKey, minutes) {
      if (!this.canCreate) {
        return
      }
      const payload = {}
      if (this.fieldHasTime) {
        payload[`field_${this.dateField.id}`] = this.composeDateTimeValue(
          dayKey,
          minutes
        )
        if (this.endTimeField) {
          payload[`field_${this.endTimeField.id}`] = this.composeDateTimeValue(
            dayKey,
            Math.min(MINUTES_PER_DAY, minutes + DEFAULT_EVENT_DURATION)
          )
        }
      } else {
        payload[`field_${this.dateField.id}`] = dayKey
      }
      try {
        const { data } = await RowService(this.$client).create(
          this.table.id,
          payload
        )
        this.rows.push(data)
        this.totalCount += 1
        this.$nextTick(() => this.$refs.rowEditModal.show(data.id))
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    onTimedDragStart(ev, domEvent) {
      if (!this.canEditDate || this.resizing) {
        if (domEvent.preventDefault) {
          domEvent.preventDefault()
        }
        return
      }
      this.draggedRow = ev.row
      this.timedDragDuration = Math.max(SNAP_MINUTES, ev.endMin - ev.startMin)
      const col = domEvent.target.closest('.kol-calendar__tg-daycol')
      if (col) {
        const rawMin =
          ((domEvent.clientY - col.getBoundingClientRect().top) /
            this.hourHeight) *
          60
        this.timedGrabMin = Math.max(0, rawMin - ev.startMin)
      } else {
        this.timedGrabMin = 0
      }
      if (domEvent.dataTransfer) {
        domEvent.dataTransfer.effectAllowed = 'move'
        domEvent.dataTransfer.setData('text/plain', String(ev.row.id))
      }
    },
    onTimedDragOver(d, domEvent) {
      if (!this.draggedRow) {
        return
      }
      domEvent.preventDefault()
      if (domEvent.dataTransfer) {
        domEvent.dataTransfer.dropEffect = 'move'
      }
      this.dragOverDayKey = d.key
    },
    async onTimedDrop(d, domEvent) {
      domEvent.preventDefault()
      const row = this.draggedRow
      this.dragOverDayKey = null
      this.draggedRow = null
      this.suppressSlotClick = true
      setTimeout(() => {
        this.suppressSlotClick = false
      }, 50)
      if (!row || !this.canEditDate) {
        return
      }
      if (!this.fieldHasTime) {
        // pole bez czasu → tylko zmiana dnia
        return this.moveRowToDay(row, d.key)
      }
      const rect = domEvent.currentTarget.getBoundingClientRect()
      let raw =
        ((domEvent.clientY - rect.top) / this.hourHeight) * 60 -
        this.timedGrabMin
      raw = Math.max(0, Math.min(MINUTES_PER_DAY - SNAP_MINUTES, raw))
      const newStart = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
      const updates = [
        {
          field: this.dateField,
          value: this.composeDateTimeValue(d.key, newStart),
        },
      ]
      if (this.endTimeField) {
        const newEnd = Math.min(
          MINUTES_PER_DAY,
          newStart + this.timedDragDuration
        )
        updates.push({
          field: this.endTimeField,
          value: this.composeDateTimeValue(d.key, newEnd),
        })
      }
      try {
        await this.updateRowFields(row, updates)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    onTimedDragEnd() {
      this.draggedRow = null
      this.dragOverDayKey = null
    },
    async moveRowToDay(row, dayKey) {
      const value = this.composeDateValue(dayKey, row[`field_${this.dateField.id}`])
      try {
        await this.updateRowField(row, this.dateField, value)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    // --- Resize (zmiana godziny konca) wymaga endTimeField -------------------
    startResize(ev, d, domEvent) {
      if (!this.canResizeTime) {
        return
      }
      const col = domEvent.target.closest('.kol-calendar__tg-daycol')
      this.resizing = {
        rowId: ev.row.id,
        row: ev.row,
        dayKey: d.key,
        startMin: ev.startMin,
        colEl: col,
      }
      this.resizePreview = { rowId: ev.row.id, endMin: ev.endMin }
      window.addEventListener('mousemove', this.onResizeMove)
      window.addEventListener('mouseup', this.onResizeEnd)
    },
    onResizeMove(e) {
      if (!this.resizing || !this.resizing.colEl) {
        return
      }
      const rect = this.resizing.colEl.getBoundingClientRect()
      let raw = ((e.clientY - rect.top) / this.hourHeight) * 60
      let endMin = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
      endMin = Math.max(
        this.resizing.startMin + SNAP_MINUTES,
        Math.min(MINUTES_PER_DAY, endMin)
      )
      this.resizePreview = { rowId: this.resizing.rowId, endMin }
    },
    async onResizeEnd() {
      window.removeEventListener('mousemove', this.onResizeMove)
      window.removeEventListener('mouseup', this.onResizeEnd)
      const r = this.resizing
      const preview = this.resizePreview
      this.resizing = null
      this.suppressSlotClick = true
      setTimeout(() => {
        this.suppressSlotClick = false
      }, 50)
      if (!r || !preview || !this.endTimeField) {
        this.resizePreview = null
        return
      }
      try {
        await this.updateRowFields(r.row, [
          {
            field: this.endTimeField,
            value: this.composeDateTimeValue(r.dayKey, preview.endMin),
          },
        ])
      } catch (error) {
        notifyIf(error, 'field')
      }
      this.resizePreview = null
    },
    // Zapis wielu pol naraz (np. start+koniec przy przeciaganiu).
    async updateRowFields(row, updates) {
      const payload = {}
      for (const u of updates) {
        const fieldType = this.$registry.get('field', u.field.type)
        payload[`field_${u.field.id}`] = fieldType.prepareValueForUpdate(
          u.field,
          u.value
        )
      }
      const { data } = await RowService(this.$client).update(
        this.table.id,
        row.id,
        payload
      )
      this.replaceRowLocally(data)
      return data
    },
    keyToDate(key) {
      const [y, m, d] = key.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
    // Układ kolumn dla nakładających się wydarzeń w siatce godzinowej.
    layoutTimedColumns(list) {
      // grupy wydarzeń, które się nakładają w czasie
      let clusterEnd = -1
      let cluster = []
      const flush = () => {
        if (cluster.length === 0) {
          return
        }
        // przydziel kolumny zachłannie
        const colEnds = []
        for (const ev of cluster) {
          let col = 0
          while (col < colEnds.length && colEnds[col] > ev.startMin) {
            col++
          }
          colEnds[col] = ev.endMin
          ev._col = col
        }
        const cols = colEnds.length
        for (const ev of cluster) {
          ev.leftPct = (ev._col / cols) * 100
          ev.widthPct = (1 / cols) * 100
        }
        cluster = []
      }
      for (const ev of list) {
        if (ev.startMin >= clusterEnd && cluster.length > 0) {
          flush()
        }
        cluster.push(ev)
        clusterEnd = Math.max(clusterEnd, ev.endMin)
      }
      flush()
    },
    openRow(row) {
      this.$refs.rowEditModal.show(row.id)
    },
    // --- Zapis wartosci wiersza (modal @update oraz drag&drop daty) ----------
    async updateRowField(row, field, value) {
      const fieldType = this.$registry.get('field', field.type)
      const prepared = fieldType.prepareValueForUpdate(field, value)
      const payload = { [`field_${field.id}`]: prepared }
      const { data } = await RowService(this.$client).update(
        this.table.id,
        row.id,
        payload
      )
      this.replaceRowLocally(data)
      return data
    },
    async updateValue({ row, field, value }) {
      try {
        await this.updateRowField(row, field, value)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    replaceRowLocally(updated) {
      const index = this.rows.findIndex((r) => r !== null && r.id === updated.id)
      if (index !== -1) {
        // Nowa referencja obiektu — watcher `rows` w RowEditModal podtrzyma
        // reaktywnosc (rowModal/replace), a siatka przeliczy eventsByDay.
        this.rows.splice(index, 1, updated)
      } else {
        this.rows.push(updated)
      }
    },
    async refreshRow(row) {
      try {
        const { data } = await RowService(this.$client).get(this.table.id, row.id)
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    // --- Drag & drop wydarzenia na inny dzien --------------------------------
    onEventDragStart(row) {
      if (!this.canEditDate) {
        return
      }
      this.draggedRow = row
    },
    onEventDragEnd() {
      this.draggedRow = null
      this.dragOverDayKey = null
    },
    onDayDragOver(event, day) {
      if (!this.draggedRow) {
        return
      }
      // preventDefault pozwala na drop.
      event.preventDefault()
      this.dragOverDayKey = day.key
    },
    onDayDragLeave(day) {
      if (this.dragOverDayKey === day.key) {
        this.dragOverDayKey = null
      }
    },
    async onDayDrop(day) {
      const row = this.draggedRow
      this.dragOverDayKey = null
      this.draggedRow = null
      if (!row || !this.canEditDate) {
        return
      }
      const current = this.parseDateValue(row[`field_${this.dateField.id}`])
      if (current && current.dayKey === day.key) {
        return
      }
      const value = this.composeDateValue(
        day.key,
        row[`field_${this.dateField.id}`]
      )
      try {
        await this.updateRowField(row, this.dateField, value)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    // --- Tworzenie wydarzenia w danym dniu -----------------------------------
    async createEventOnDay(day) {
      if (!this.canCreate) {
        return
      }
      const value = this.composeDateValue(day.key, null)
      const payload = { [`field_${this.dateField.id}`]: value }
      try {
        const { data } = await RowService(this.$client).create(
          this.table.id,
          payload
        )
        this.rows.push(data)
        this.totalCount += 1
        this.$nextTick(() => this.$refs.rowEditModal.show(data.id))
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    // --- Pomocnicze: data/godzina --------------------------------------------
    composeDateValue(dayKey, originalValue) {
      if (!this.fieldHasTime) {
        return dayKey
      }
      const [year, month, dayNum] = dayKey.split('-').map(Number)
      let hours = DEFAULT_NEW_EVENT_HOUR
      let minutes = 0
      if (originalValue) {
        const orig = new Date(originalValue)
        if (!isNaN(orig.getTime())) {
          hours = orig.getHours()
          minutes = orig.getMinutes()
        }
      }
      // Budujemy lokalny czas i konwertujemy do ISO (UTC) do zapisu.
      return new Date(year, month - 1, dayNum, hours, minutes, 0).toISOString()
    },
    visibleEvents(day) {
      const events = this.eventsByDay[day.key] || []
      if (this.expandedDays[day.key]) {
        return events
      }
      return events.slice(0, VISIBLE_EVENTS_PER_DAY)
    },
    hiddenCount(day) {
      if (this.expandedDays[day.key]) {
        return 0
      }
      const events = this.eventsByDay[day.key] || []
      return Math.max(0, events.length - VISIBLE_EVENTS_PER_DAY)
    },
    toggleExpand(dayKey) {
      this.expandedDays = {
        ...this.expandedDays,
        [dayKey]: !this.expandedDays[dayKey],
      }
    },
    rowTitle(row) {
      const field = this.primaryField
      if (!field) {
        return ''
      }
      const fieldType = this.$registry.get('field', field.type)
      const value = fieldType.toHumanReadableString(
        field,
        row[`field_${field.id}`]
      )
      return value || this.$t('keyoflifeViews.unnamed')
    },
    parseDateValue(value) {
      if (!value || typeof value !== 'string') {
        return null
      }
      if (!this.fieldHasTime) {
        return { dayKey: value.slice(0, 10), time: null }
      }
      const date = new Date(value)
      if (isNaN(date.getTime())) {
        return null
      }
      const pad = (number) => String(number).padStart(2, '0')
      return {
        dayKey: this.dayKeyFromDate(date),
        time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
      }
    },
    dayKeyFromDate(date) {
      const pad = (number) => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`
    },
    isoWeekNumber(date) {
      const target = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      )
      const dayNumber = (target.getUTCDay() + 6) % 7
      target.setUTCDate(target.getUTCDate() - dayNumber + 3)
      const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4))
      const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7
      firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3)
      return 1 + Math.round((target - firstThursday) / (7 * 86400000))
    },
  },
}
</script>
